import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Navigator from './components/Navigation';
import LifeTodoToggle from './components/LifeTodoToggle';
import ProtectedSection from './components/ProtectedSection';
import { hashPasswordsFromEnv } from './utils/passwordUtils';
import { useMetaTags } from './utils/useMetaTags';
import { supabase, LifeItem, TodoGroup } from './lib/supabaseClient';

function PageTodo(props: {
  activeNav: number;
  setActiveNav: Dispatch<SetStateAction<number>>;
}) {
  useMetaTags({
    title: 'Todo - Lynkim',
    description: 'Todo',
    image: '/ogimage.png',
  });

  // /todo 페이지는 항상 다크 모드로 강제 적용 (다른 페이지로 떠나면 이전 값 복구)
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.getAttribute('data-theme');
    html.setAttribute('data-theme', 'dark');
    return () => {
      if (prev) html.setAttribute('data-theme', prev);
      else html.removeAttribute('data-theme');
    };
  }, []);

  return (
    <>
      <Navigator activeNav={props.activeNav} setActiveNav={props.setActiveNav} />
      <div className='pageTitle'>To<span style={{ letterSpacing: '1px' }}>d</span>o</div>
      <ProtectedSection
        lockPassword={hashPasswordsFromEnv(process.env.REACT_APP_TODO_SECTION_PASSWORDS)}
        description="개인적으로 할 일을 관리하는 페이지예요"
        hideHint
      >
        <TodoBoard />
      </ProtectedSection>
      <LifeTodoToggle />
    </>
  );
}

function TodoBoard() {
  const [groups, setGroups] = useState<TodoGroup[]>([]);
  const [items, setItems] = useState<LifeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 새로 추가된 항목 id — 해당 TodoRow가 마운트되면 자동 포커스
  const [focusItemId, setFocusItemId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [groupsRes, itemsRes] = await Promise.all([
        supabase
          .from('todo_groups')
          .select('*')
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase
          .from('life_items')
          .select('*')
          .not('group_id', 'is', null)
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
      ]);
      if (cancelled) return;
      if (groupsRes.error) setError(groupsRes.error.message);
      if (itemsRes.error) setError(itemsRes.error.message);
      setGroups((groupsRes.data ?? []) as TodoGroup[]);
      setItems((itemsRes.data ?? []) as LifeItem[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const itemsByGroup = useMemo(() => {
    const map = new Map<string, LifeItem[]>();
    for (const it of items) {
      if (!it.group_id) continue;
      const arr = map.get(it.group_id) ?? [];
      arr.push(it);
      map.set(it.group_id, arr);
    }
    // 체크된 항목이 그룹 내에서 위로 오도록 정렬.
    // 같은 체크 상태 내에서는 기존 position → created_at 순서 유지.
    map.forEach((arr) => {
      arr.sort((a: LifeItem, b: LifeItem) => {
        if (a.checked !== b.checked) return a.checked ? -1 : 1;
        if (a.position !== b.position) return a.position - b.position;
        return a.created_at.localeCompare(b.created_at);
      });
    });
    return map;
  }, [items]);

  const handleAddGroup = useCallback(async () => {
    const nextPosition = groups.length > 0 ? Math.max(...groups.map((g) => g.position)) + 1 : 0;
    const { data, error: err } = await supabase
      .from('todo_groups')
      .insert({ title: '', position: nextPosition })
      .select()
      .single();
    if (err) {
      setError(err.message);
      return;
    }
    if (data) setGroups((prev) => [...prev, data as TodoGroup]);
  }, [groups]);

  const handleGroupTitleCommit = useCallback(async (id: string, title: string) => {
    setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, title } : g)));
    const { error: err } = await supabase
      .from('todo_groups')
      .update({ title, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) setError(err.message);
  }, []);

  const handleDeleteGroup = useCallback(async (id: string) => {
    if (!window.confirm('이 그룹을 삭제할까요? 안의 항목도 모두 사라져요.')) return;
    setGroups((prev) => prev.filter((g) => g.id !== id));
    setItems((prev) => prev.filter((it) => it.group_id !== id));
    const { error: err } = await supabase.from('todo_groups').delete().eq('id', id);
    if (err) setError(err.message);
  }, []);

  const handleAddItem = useCallback(
    async (groupId: string) => {
      const groupItems = itemsByGroup.get(groupId) ?? [];
      const nextPosition =
        groupItems.length > 0 ? Math.max(...groupItems.map((i) => i.position)) + 1 : 0;
      const { data, error: err } = await supabase
        .from('life_items')
        .insert({ text: '', checked: false, position: nextPosition, group_id: groupId })
        .select()
        .single();
      if (err) {
        setError(err.message);
        return;
      }
      if (data) {
        setItems((prev) => [...prev, data as LifeItem]);
        setFocusItemId((data as LifeItem).id);
      }
    },
    [itemsByGroup]
  );

  const handleAutoFocusConsumed = useCallback(() => {
    setFocusItemId(null);
  }, []);

  const handleToggleItem = useCallback(async (item: LifeItem) => {
    const next = !item.checked;
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, checked: next } : it)));
    const { error: err } = await supabase
      .from('life_items')
      .update({ checked: next, updated_at: new Date().toISOString() })
      .eq('id', item.id);
    if (err) {
      setItems((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, checked: item.checked } : it))
      );
      setError(err.message);
    }
  }, []);

  const handleItemTextChange = useCallback((id: string, text: string) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, text } : it)));
  }, []);

  const handleItemTextCommit = useCallback(async (id: string, text: string) => {
    const { error: err } = await supabase
      .from('life_items')
      .update({ text, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) setError(err.message);
  }, []);

  const handleDeleteItem = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    const { error: err } = await supabase.from('life_items').delete().eq('id', id);
    if (err) setError(err.message);
  }, []);

  // 빈 입력에서 Backspace 시: 행 삭제하고 같은 그룹의 바로 위 항목으로 포커스 이동
  const handleBackspaceEmpty = useCallback(
    async (item: LifeItem) => {
      const groupItems = item.group_id ? itemsByGroup.get(item.group_id) ?? [] : [];
      const idx = groupItems.findIndex((it) => it.id === item.id);
      const prevItem = idx > 0 ? groupItems[idx - 1] : null;

      setItems((prev) => prev.filter((it) => it.id !== item.id));
      if (prevItem) setFocusItemId(prevItem.id);

      const { error: err } = await supabase.from('life_items').delete().eq('id', item.id);
      if (err) setError(err.message);
    },
    [itemsByGroup]
  );

  if (loading) {
    return <div className='todoBoard' />;
  }

  return (
    <div className='todoBoard'>
      {error && (
        <div
          style={{
            color: 'var(--color-danger)',
            background: 'var(--color-danger-soft)',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <div className='todoGrid'>
        {groups.map((group) => (
          <TodoGroupCard
            key={group.id}
            group={group}
            items={itemsByGroup.get(group.id) ?? []}
            focusItemId={focusItemId}
            onTitleCommit={(title) => handleGroupTitleCommit(group.id, title)}
            onDeleteGroup={() => handleDeleteGroup(group.id)}
            onAddItem={() => handleAddItem(group.id)}
            onToggleItem={handleToggleItem}
            onItemTextChange={handleItemTextChange}
            onItemTextCommit={handleItemTextCommit}
            onDeleteItem={handleDeleteItem}
            onBackspaceEmpty={handleBackspaceEmpty}
            onAutoFocusConsumed={handleAutoFocusConsumed}
          />
        ))}
      </div>

      <button className='todoAddGroupBtn' onClick={handleAddGroup}>
        + 새 그룹
      </button>
    </div>
  );
}

function TodoGroupCard(props: {
  group: TodoGroup;
  items: LifeItem[];
  focusItemId: string | null;
  onTitleCommit: (title: string) => void;
  onDeleteGroup: () => void;
  onAddItem: () => void;
  onToggleItem: (item: LifeItem) => void;
  onItemTextChange: (id: string, text: string) => void;
  onItemTextCommit: (id: string, text: string) => void;
  onDeleteItem: (id: string) => void;
  onBackspaceEmpty: (item: LifeItem) => void;
  onAutoFocusConsumed: () => void;
}) {
  const { group, items } = props;
  const [title, setTitle] = useState(group.title);
  const [hoveredGroup, setHoveredGroup] = useState(false);

  useEffect(() => {
    setTitle(group.title);
  }, [group.title]);

  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    },
    []
  );

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setTitle(v);
    if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    titleTimerRef.current = setTimeout(() => props.onTitleCommit(v), 500);
  };

  return (
    <div
      className='todoCard fadeInUp'
      onMouseEnter={() => setHoveredGroup(true)}
      onMouseLeave={() => setHoveredGroup(false)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type='text'
          className='todoCardTitle'
          value={title}
          placeholder='그룹 이름'
          onChange={handleTitleChange}
          onBlur={(e) => props.onTitleCommit(e.target.value)}
        />
        <button
          onClick={props.onDeleteGroup}
          aria-label='그룹 삭제'
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '4px 6px',
            color: 'var(--color-text-disabled)',
            opacity: hoveredGroup ? 1 : 0,
            transition: 'opacity 0.15s ease',
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map((item) => (
          <TodoRow
            key={item.id}
            item={item}
            autoFocus={props.focusItemId === item.id}
            onAutoFocusConsumed={props.onAutoFocusConsumed}
            onToggle={() => props.onToggleItem(item)}
            onTextChange={(text) => props.onItemTextChange(item.id, text)}
            onTextCommit={(text) => props.onItemTextCommit(item.id, text)}
            onDelete={() => props.onDeleteItem(item.id)}
            onEnter={props.onAddItem}
            onBackspaceEmpty={() => props.onBackspaceEmpty(item)}
          />
        ))}
      </div>

      <button
        onClick={props.onAddItem}
        style={{
          alignSelf: 'flex-start',
          marginTop: '4px',
          background: 'none',
          border: 'none',
          padding: '6px 0',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: 600,
          color: 'var(--color-text-disabled)',
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--color-text-primary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--color-text-disabled)';
        }}
      >
        + 항목 추가
      </button>
    </div>
  );
}

function TodoRow(props: {
  item: LifeItem;
  onToggle: () => void;
  onTextChange: (text: string) => void;
  onTextCommit: (text: string) => void;
  onDelete: () => void;
  onEnter: () => void;
  onBackspaceEmpty: () => void;
  autoFocus?: boolean;
  onAutoFocusConsumed?: () => void;
}) {
  const { item, onToggle, onTextChange, onTextCommit, onDelete, onEnter, onBackspaceEmpty, autoFocus, onAutoFocusConsumed } = props;
  const [hovered, setHovered] = useState(false);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(
    () => () => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    },
    []
  );

  // 새로 추가된 항목이면 input에 포커스
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      onAutoFocusConsumed?.();
    }
  }, [autoFocus, onAutoFocusConsumed]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onTextChange(v);
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => onTextCommit(v), 500);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // 한글 IME 조합 중에는 모두 무시

    // ⌘/Ctrl + Enter → 체크 토글
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onToggle();
      return;
    }

    // Enter → 새 항목 추가 (디바운스 대기 중인 텍스트 즉시 커밋 후)
    if (e.key === 'Enter') {
      e.preventDefault();
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      onTextCommit(e.currentTarget.value);
      onEnter();
      return;
    }

    // 빈 입력에서 Backspace → 행 삭제 + 이전 행 포커스
    if (e.key === 'Backspace' && e.currentTarget.value === '') {
      e.preventDefault();
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      onBackspaceEmpty();
      return;
    }
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '0',
      }}
    >
      <input
        type='checkbox'
        className='todoCheckbox'
        checked={item.checked}
        onChange={onToggle}
      />
      <input
        ref={inputRef}
        type='text'
        className='todoRowInput'
        value={item.text}
        placeholder='할 일'
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={(e) => onTextCommit(e.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: '17px',
          padding: '4px 0',
          color: item.checked ? 'var(--color-text-disabled)' : 'var(--color-text-body)',
          textDecoration: item.checked ? 'line-through' : 'none',
          fontFamily: 'inherit',
        }}
      />
      <button
        onClick={onDelete}
        aria-label='삭제'
        style={{
          background: 'none',
          border: 'none',
          padding: '2px 6px',
          fontSize: '12px',
          cursor: 'pointer',
          color: 'var(--color-text-disabled)',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.15s ease',
        }}
      >
        ✕
      </button>
    </div>
  );
}

export default PageTodo;
